/**
 * ══════════════════════════════════════════════════════════════════════════════
 * FC.10 — CotejoExportService
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * Genera datos de exportación del cotejo de completitud:
 *   - Filas para pestaña XLSX «Cotejo_Completitud»
 *   - Fragmento HTML para informe resumen
 *
 * Se integra con XLSXExporter (F5.02) y HTMLReportExporter (F5.05) existentes.
 * No depende de ExcelJS directamente — produce arrays planos que el exportador
 * consume para crear la hoja.
 *
 * @module services/cotejo/CotejoExportService
 * @version 1.0.0
 */

import type {
  ReferenciaNormativa,
  ResultadoCotejo,
  InfraAusente,
  MatchConfirmado,
  InfraSobrante,
  CriticidadLegal,
  EstadisticasCotejo as _EstadisticasCotejo,
  AusenteDecidido,
} from './types';
import { TIPOLOGIAS } from '@/constants/export.constants';
import type { TipologiaPTEL } from '@/types/tipologia';
import type { AlmacenDecisiones } from '@/hooks/useDecisionesFila';

// ─────────────────────────────────────────────────────────────────────────────
// C0-d · P4 LEE LO DECIDIDO EN P3
// ─────────────────────────────────────────────────────────────────────────────

/**
 * C0-d — Cotejo que se pasa a P4 con lo decidido en P3 (DOP selladas por Luis el
 * 24-oct-2026). Sobre los ausentes, por id de la fuente:
 * - «Añadir al PTEL» (`propuesto`): sale de `ausentes` y va a
 *   `decisionesTecnico.propuestos`; el informe lo cuenta como incluido (DOP-1 a).
 *   No crea fila en la exportación.
 * - «Ignorar» (`descartado`): sale de `ausentes` y va a
 *   `decisionesTecnico.descartados`, con la fecha (DOP-2 a).
 * - «Ignorar» una fila del plan (`oculto`) no cambia nada en P4 (DOP-3 a).
 * Cobertura: (emparejados + propuestos) / (catálogo − descartados), con el mismo
 * redondeo que `CotejoCompletitudService`. No toca el cotejo de entrada (P3 sigue
 * con el suyo) y, sin decisiones sobre ausentes, devuelve el mismo objeto.
 */
export function aplicarDecisionesAlResultado(
  resultado: ResultadoCotejo,
  almacen: AlmacenDecisiones,
): ResultadoCotejo {
  const restantes: InfraAusente[] = [];
  const propuestos: AusenteDecidido[] = [];
  const descartados: AusenteDecidido[] = [];
  for (const ausente of resultado.ausentes) {
    const decision = almacen[ausente.candidato.idFuente];
    if (decision?.valor === 'propuesto') propuestos.push({ ausente, fecha: decision.fecha });
    else if (decision?.valor === 'descartado') descartados.push({ ausente, fecha: decision.fecha });
    else restantes.push(ausente);
  }
  if (propuestos.length === 0 && descartados.length === 0) return resultado;

  const e = resultado.estadisticas;
  const catalogo = e.totalReferencia - descartados.length;
  const ratioCoberturaGlobal = catalogo > 0
    ? Math.round(((resultado.matches.length + propuestos.length) / catalogo) * 100) / 100
    : 1;
  return {
    ...resultado,
    ausentes: restantes,
    estadisticas: {
      ...e,
      ausentesCriticos: restantes.filter(a => a.estado === 'ausente_critico').length,
      ausentesRecomendados: restantes.filter(a => a.estado === 'ausente_recomendado').length,
      ratioCoberturaGlobal,
    },
    decisionesTecnico: { propuestos, descartados },
  };
}

/** C0-d: escapa el texto antes de meterlo en el HTML (como `esc` de `HTMLReportExporter`). */
const esc = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** C0-d: fecha de la decisión como dd/mm/aaaa, en la hora de Andalucía. */
const fechaDecision = (iso: string): string =>
  new Date(iso).toLocaleDateString('es-ES', {
    timeZone: 'Europe/Madrid', day: '2-digit', month: '2-digit', year: 'numeric',
  });

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTES
// ─────────────────────────────────────────────────────────────────────────────

/** Columnas de la pestaña XLSX Cotejo_Completitud */
export const COTEJO_COLUMNS = [
  { header: 'Estado',       key: 'estado',     width: 18 },
  { header: 'Criticidad',   key: 'criticidad', width: 12 },
  { header: 'Nombre',       key: 'nombre',     width: 35 },
  { header: 'Tipología',    key: 'tipologia',  width: 16 },
  { header: 'Fuente',       key: 'fuente',     width: 14 },
  { header: 'Norma',        key: 'norma',      width: 28 },
  { header: 'X (EPSG:25830)', key: 'x25830',   width: 14 },
  { header: 'Y (EPSG:25830)', key: 'y25830',   width: 14 },
  { header: 'Aforo',        key: 'aforo',      width: 12 },
  { header: 'Match PTEL',   key: 'matchPTEL',  width: 20 },
  { header: 'Score',        key: 'score',      width: 8 },
] as const;

const CRITICIDAD_ORDEN: Record<CriticidadLegal, number> = {
  critica: 0, alta: 1, media: 2, baja: 3,
};

const CRITICIDAD_LABEL: Record<CriticidadLegal, string> = {
  critica: '🔴 Crítica', alta: '🟠 Alta', media: '🟡 Media', baja: '🟢 Baja',
};

/**
 * NORMA-b — Texto de la referencia normativa tal como puede enseñarse.
 * Con confianza 'baja' no se enseña nada: misma regla que la insignia de P3.
 */
export function textoNorma(ref: ReferenciaNormativa): string {
  if (ref.confianza === 'baja') return '';
  return `${ref.norma} ${ref.articulo}`.trim();
}

const ESTADO_LABEL: Record<string, string> = {
  ausente_critico: 'Ausente (crítico)',
  ausente_recomendado: 'Ausente (recomendado)',
  incluido: 'Incluido',
  probable: 'Probable',
  sobrante: 'Sobrante',
};

// ─────────────────────────────────────────────────────────────────────────────
// TIPOS EXPORTACIÓN
// ─────────────────────────────────────────────────────────────────────────────

/** Fila plana para XLSX — un valor por columna en orden de COTEJO_COLUMNS */
export type CotejoRow = (string | number | null)[];

export interface CotejoExportData {
  /** Headers (primera fila) */
  readonly headers: string[];
  /** Filas de datos */
  readonly rows: CotejoRow[];
  /** HTML del fragmento cotejo para informe */
  readonly htmlFragment: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// SERVICIO
// ─────────────────────────────────────────────────────────────────────────────

export class CotejoExportService {

  /**
   * Genera datos de exportación a partir de un ResultadoCotejo.
   * Ausentes van primero, ordenados por criticidad descendente.
   */
  static generate(resultado: ResultadoCotejo): CotejoExportData {
    const headers = COTEJO_COLUMNS.map(c => c.header);
    const rows: CotejoRow[] = [];

    // 1. Ausentes (ordenados por criticidad)
    const sortedAusentes = [...resultado.ausentes].sort(
      (a, b) => CRITICIDAD_ORDEN[a.criticidad] - CRITICIDAD_ORDEN[b.criticidad],
    );
    for (const aus of sortedAusentes) {
      rows.push(this.ausenteToRow(aus));
    }

    // 2. Matches (incluidos + probables)
    for (const match of resultado.matches) {
      rows.push(this.matchToRow(match));
    }

    // 3. Sobrantes
    for (const sob of resultado.sobrantes) {
      rows.push(this.sobranteToRow(sob));
    }

    const htmlFragment = this.generateHTML(resultado);

    return { headers, rows, htmlFragment };
  }

  // ─── Conversores a fila ──────────────────────────────────────────────

  private static ausenteToRow(aus: InfraAusente): CotejoRow {
    const c = aus.candidato;
    const norma = textoNorma(aus.referenciaNormativa) || null;
    const aforo = c.metadata?.aforo
      ? `${c.metadata.aforo} ${c.metadata.aforoUnidad ?? ''}`
      : null;
    return [
      ESTADO_LABEL[aus.estado] ?? aus.estado,
      CRITICIDAD_LABEL[aus.criticidad] ?? aus.criticidad,
      c.nombre,
      c.tipologia,
      c.fuente.replace(/_/g, ' '),
      norma,
      c.x25830 ? +c.x25830.toFixed(1) : null,
      c.y25830 ? +c.y25830.toFixed(1) : null,
      aforo,
      null, // sin match PTEL
      null, // sin score
    ];
  }

  private static matchToRow(m: MatchConfirmado): CotejoRow {
    const c = m.candidato;
    return [
      ESTADO_LABEL[m.estado] ?? m.estado,
      null, // no aplica criticidad
      c.nombre,
      c.tipologia,
      c.fuente.replace(/_/g, ' '),
      null, // no aplica norma
      c.x25830 ? +c.x25830.toFixed(1) : null,
      c.y25830 ? +c.y25830.toFixed(1) : null,
      null, // aforo no relevante
      m.infraPTEL.nombre,
      m.score.total,
    ];
  }

  private static sobranteToRow(sob: InfraSobrante): CotejoRow {
    return [
      ESTADO_LABEL.sobrante,
      null,
      sob.infraPTEL.nombre,
      (sob.infraPTEL as { tipo?: string }).tipo ?? null,
      null, // sin fuente
      null, // sin norma
      null, null, // sin coords referencia
      null,
      sob.infraPTEL.nombre,
      null,
    ];
  }

  // ─── HTML ────────────────────────────────────────────────────────────

  private static generateHTML(resultado: ResultadoCotejo): string {
    const e = resultado.estadisticas;
    const ausentesTotal = e.ausentesCriticos + e.ausentesRecomendados;
    const cobertura = Math.round(e.ratioCoberturaGlobal * 100);
    // C0-d: lo que el técnico añadió en P3 cuenta como incluido (DOP-1 a).
    const propuestos = resultado.decisionesTecnico?.propuestos ?? [];
    const descartados = resultado.decisionesTecnico?.descartados ?? [];
    const notaPropuestos = propuestos.length === 0 ? ''
      : ` (${propuestos.length} ${propuestos.length === 1 ? 'propuesto' : 'propuestos'} por el técnico)`;

    let html = `<section class="cotejo">
<h2>Cotejo con el catálogo oficial</h2>
<p><strong>${e.incluidos + e.probables + propuestos.length}</strong> incluidos${notaPropuestos} · 
<strong style="color:#EF4444">${ausentesTotal}</strong> ausentes
(${e.ausentesCriticos} críticos, ${e.ausentesRecomendados} recomendados) · 
<strong style="color:#60A5FA">${e.sobrantes}</strong> sobrantes · 
Cobertura del catálogo: <strong>${cobertura}%</strong></p>\n`;

    if (ausentesTotal > 0) {
      const conNorma = resultado.ausentes.some(a => textoNorma(a.referenciaNormativa) !== '');
      html += `<table><thead><tr>
<th>Estado</th><th>Nombre</th><th>Tipología</th><th>Fuente</th>${conNorma ? '<th>Norma</th>' : ''}
</tr></thead><tbody>\n`;

      const sorted = [...resultado.ausentes].sort(
        (a, b) => CRITICIDAD_ORDEN[a.criticidad] - CRITICIDAD_ORDEN[b.criticidad],
      );
      for (const aus of sorted) {
        const colorBg = aus.criticidad === 'critica' || aus.criticidad === 'alta'
          ? '#FEF2F2' : '#FFFBEB';
        html += `<tr style="background:${colorBg}">
<td>${CRITICIDAD_LABEL[aus.criticidad]}</td>
<td>${aus.candidato.nombre}</td>
<td>${TIPOLOGIAS[aus.candidato.tipologia as TipologiaPTEL]?.label ?? aus.candidato.tipologia}</td>
<td>${aus.candidato.fuente.replace(/_/g, ' ')}</td>
${conNorma ? `<td>${textoNorma(aus.referenciaNormativa)}</td>\n` : ''}</tr>\n`;
      }
      html += `</tbody></table>\n`;
    }

    // C0-d: listas de lo decidido por el técnico en P3, con la fecha y sin el
    // `uid` (DOP-2 a). Los nombres van escapados.
    html += this.tablaDecididos('Propuestos por el técnico', propuestos);
    html += this.tablaDecididos('Descartados por el técnico', descartados);

    html += `</section>`;
    return html;
  }

  private static tablaDecididos(titulo: string, decididos: readonly AusenteDecidido[]): string {
    if (decididos.length === 0) return '';
    const filas = decididos.map(({ ausente, fecha }) => {
      const c = ausente.candidato;
      const tipologia = TIPOLOGIAS[c.tipologia as TipologiaPTEL]?.label ?? c.tipologia;
      return `<tr><td>${esc(c.nombre)}</td><td>${esc(String(tipologia))}</td>` +
        `<td>${esc(c.fuente.replace(/_/g, ' '))}</td><td>${fechaDecision(fecha)}</td></tr>\n`;
    }).join('');
    return `<h3>${titulo}</h3>
<table><thead><tr>
<th>Nombre</th><th>Tipología</th><th>Fuente</th><th>Fecha de la decisión</th>
</tr></thead><tbody>\n${filas}</tbody></table>\n`;
  }
}

export default CotejoExportService;
